import { useState, useContext, useEffect, useRef } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import posthog from "@/lib/posthog";
import MerchantContext from "../../../contexts/MerchantContext";
import MainSectionSection from "../../../components/MainSectionSection";
import SectionTitle from "../../../components/SectionTitle";
import MoveInDateRangeSelector from "../../../components/MoveInDateRangeSelector";
import AnalyticsAppointmentsContainer from "./AnalyticsAppointmentsContainer";
import AnalyticsDemandContainer from "./AnalyticsDemandContainer";
import AnalyticsLeadCategoriesContainer from "./AnalyticsLeadCategoriesContainer";
import AnalyticsAdminContainer from "./AnalyticsAdminContainer";
import AnalyticsBasicPipelineContainer from "./AnalyticsBasicPipelineContainer";
import { usePipeline } from "../../../contexts/PipelineContext";
import { formatDateForInput } from "../../../utils/index";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// Human-readable labels for each analytics sub-tab, matching the TabsTrigger text.
const ANALYTICS_TAB_LABELS: Record<string, string> = {
  "agente-comercial": "Agente Comercial",
  administrativo: "Administrativo",
  mantenimiento: "Mantenimiento",
  captacion: "Captación",
  demand: "Demanda",
  "lead-categories": "Tipo de Cliente",
};

export const AnalyticsContainer = (): JSX.Element => {
  const { merchantId, merchantType, enableAnalyticsByMoveInDate, leadCategoryDefinitions } =
    useContext(MerchantContext);
  const currentDate = new Date();

  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const [startDate, setStartDate] = useState<string>(formatDateForInput(firstDayOfMonth));
  const [endDate, setEndDate] = useState<string>(formatDateForInput(currentDate));
  const [showMoveInDateFilter, setShowMoveInDateFilter] = useState<boolean>(false);
  const [moveInDateRange, setMoveInDateRange] = useState<
    { minDays: number; maxDays: number } | undefined
  >(undefined);
  const [activeTab, setActiveTab] = useState<string>("agente-comercial");

  const { member } = useStytchMember();
  const userEmail = member?.email_address;
  const userName = member?.name;
  const memberId = member?.member_id;

  // Track entering the Analytics section (fires once the merchant + user are known).
  useEffect(() => {
    if (!merchantId || !memberId) return;
    posthog.capture("analytics viewed", {
      merchant_id: merchantId,
      email: userEmail,
      name: userName,
      member_id: memberId,
    });
  }, [merchantId, memberId, userEmail, userName]);

  // Track which sub-tab is being looked at — includes the default tab on load and every switch.
  // `is_initial` is true for the tab shown on arrival, false for deliberate user switches.
  const isInitialTabRef = useRef(true);
  useEffect(() => {
    if (!merchantId || !memberId) return;
    posthog.capture("analytics tab viewed", {
      merchant_id: merchantId,
      email: userEmail,
      name: userName,
      member_id: memberId,
      tab: activeTab,
      tab_label: ANALYTICS_TAB_LABELS[activeTab] ?? activeTab,
      is_initial: isInitialTabRef.current,
    });
    isInitialTabRef.current = false;
  }, [activeTab, merchantId, memberId, userEmail, userName]);

  const {
    sourcePropertiesPipelinePositions,
    maintenancePipelinePositions,
    generalPipelinePositions,
  } = usePipeline();

  const hasGeneralPipeline = Object.keys(generalPipelinePositions ?? {}).length > 0;
  const hasMaintenancePipeline = Object.keys(maintenancePipelinePositions ?? {}).length > 0;
  const hasSourcePropertiesPipeline =
    Object.keys(sourcePropertiesPipelinePositions ?? {}).length > 0;

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);

    if (newStartDate > endDate) {
      setEndDate(newStartDate);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);

    if (newEndDate < startDate) {
      setStartDate(newEndDate);
    }
  };

  const handleToggleMoveInDateFilter = () => {
    const newShowState = !showMoveInDateFilter;
    setShowMoveInDateFilter(newShowState);

    if (newShowState) {
      setMoveInDateRange({ minDays: 0, maxDays: 45 });
    } else {
      setMoveInDateRange(undefined);
    }
  };

  const handleMoveInDateRangeChange = (minDays: number, maxDays: number) => {
    setMoveInDateRange({ minDays, maxDays });
  };

  const isLeadCategoriesTab =
    activeTab === "lead-categories" && leadCategoryDefinitions.length > 0;
  const isDemandTab = activeTab === "demand";

  return (
    <MainSectionSection>
      <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
        <div className="flex flex-col gap-1">
          <SectionTitle title="Analítica" />
        </div>
        <div className="flex flex-wrap items-end gap-4">
          {enableAnalyticsByMoveInDate && !isLeadCategoriesTab && !isDemandTab && (
            <>
              <div>
                <p className="mb-1 text-xs text-gray-secondary">Filtro mudanza</p>
                <Button
                  variant={showMoveInDateFilter ? "main" : "outline"}
                  onClick={handleToggleMoveInDateFilter}
                  className="h-10"
                >
                  <CalendarDays className="size-4" />
                  {showMoveInDateFilter ? "Ocultar" : "Mostrar"}
                </Button>
              </div>
              {showMoveInDateFilter && moveInDateRange && (
                <MoveInDateRangeSelector
                  minDays={moveInDateRange.minDays}
                  maxDays={moveInDateRange.maxDays}
                  onRangeChange={handleMoveInDateRangeChange}
                />
              )}
            </>
          )}
          <div>
            <p className="mb-1 text-xs text-gray-secondary">Fecha de inicio</p>
            <Input
              type="date"
              className="h-11 w-full bg-white md:h-10 md:w-[200px]"
              value={startDate}
              onChange={handleStartDateChange}
              max={formatDateForInput(currentDate)}
              min="2020-01-01"
            />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-secondary">Fecha de fin</p>
            <Input
              type="date"
              className="h-11 w-full bg-white md:h-10 md:w-[200px]"
              value={endDate}
              onChange={handleEndDateChange}
              max={formatDateForInput(currentDate)}
              min={startDate}
            />
          </div>
        </div>
      </div>
      <div className="relative flex grow flex-col">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex min-h-full flex-col"
        >
          <TabsList className="sticky top-0 z-10 w-full border-b border-border bg-white">
            <TabsTrigger value="agente-comercial" className="min-h-11">
              Agente Comercial
            </TabsTrigger>
            {hasGeneralPipeline && (
              <TabsTrigger value="administrativo" className="min-h-11">
                Administrativo
              </TabsTrigger>
            )}
            {hasMaintenancePipeline && (
              <TabsTrigger value="mantenimiento" className="min-h-11">
                Mantenimiento
              </TabsTrigger>
            )}
            {hasSourcePropertiesPipeline && (
              <TabsTrigger value="captacion" className="min-h-11">
                Captación
              </TabsTrigger>
            )}
            {merchantType === "rentals" && (
              <TabsTrigger value="demand" className="min-h-11">
                Demanda
              </TabsTrigger>
            )}
            {leadCategoryDefinitions.length > 0 && (
              <TabsTrigger value="lead-categories" className="min-h-11">
                Tipo de Cliente
              </TabsTrigger>
            )}
          </TabsList>
          <div className="flex-1">
            <TabsContent value="agente-comercial" className="py-4">
              <AnalyticsAppointmentsContainer
                startDate={startDate}
                endDate={endDate}
                merchantId={merchantId}
                moveInDateRange={enableAnalyticsByMoveInDate ? moveInDateRange : undefined}
              />
            </TabsContent>
            {hasGeneralPipeline && (
              <TabsContent value="administrativo" className="py-4">
                <AnalyticsAdminContainer
                  startDate={startDate}
                  endDate={endDate}
                  merchantId={merchantId}
                />
              </TabsContent>
            )}
            {hasMaintenancePipeline && (
              <TabsContent value="mantenimiento" className="py-4">
                <AnalyticsBasicPipelineContainer
                  startDate={startDate}
                  endDate={endDate}
                  merchantId={merchantId}
                  pipelineId="maintenance"
                  successLabel="Solicitudes creadas"
                  successInfo="Leads que solicitaron un mantenimiento"
                  showTicketStatus
                />
              </TabsContent>
            )}
            {hasSourcePropertiesPipeline && (
              <TabsContent value="captacion" className="py-4">
                <AnalyticsBasicPipelineContainer
                  startDate={startDate}
                  endDate={endDate}
                  merchantId={merchantId}
                  pipelineId="sourceproperties"
                  successLabel="Propietarios captados"
                  successInfo="Propietarios que entregaron la información de su inmueble para que la inmobiliaria lo arriende y administre"
                  showTicketStatus={false}
                />
              </TabsContent>
            )}
            {merchantType === "rentals" && (
              <TabsContent value="demand" className="py-4">
                <AnalyticsDemandContainer
                  startDate={startDate}
                  endDate={endDate}
                  merchantId={merchantId}
                />
              </TabsContent>
            )}
            {leadCategoryDefinitions.length > 0 && (
              <TabsContent value="lead-categories" className="py-4">
                <AnalyticsLeadCategoriesContainer
                  startDate={startDate}
                  endDate={endDate}
                  merchantId={merchantId}
                  moveInDateRange={enableAnalyticsByMoveInDate ? moveInDateRange : undefined}
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>
    </MainSectionSection>
  );
};

export default AnalyticsContainer;
